/**
 * /admin/bexio/settings — Bexio Einstellungen (settings slice A–C).
 *
 * THIS FILE IS THE ORCHESTRATOR. It owns every piece of state and both requests;
 * the section below it renders what it is handed and fetches nothing. Same split
 * as `BexioSyncRunsPage`.
 *
 * TWO INDEPENDENT READS, TWO INDEPENDENT FAILURES. The configuration and the
 * connection status are separate endpoints answering separate questions, so they
 * get a loading/error pair each rather than one shared flag. A status outage must
 * not blank the configuration the operator came here to read.
 *
 * EDITABLE FOR AN ADMIN (D2). `PATCH /api/admin/bexio/config` is admin-only, so a
 * manager keeps exactly the read-only page they had — no disabled inputs, which
 * would promise an edit the endpoint refuses. The save sends ONLY changed fields
 * and takes the response as the new baseline, so no second GET is needed.
 *
 * It still offers no "Verbindung testen" and no provisioning: both would need a
 * live Bexio call, and no such endpoint exists. The six Bexio ids stay read-only
 * until D3 can offer real catalog pickers.
 *
 * NO INNER TAB BAR IN THIS SLICE. The workspace tab bar in `BexioLayout` already
 * sits directly above this content; a second bar carrying one tab would read as a
 * rendering fault, and carrying four disabled ones would be four promises the
 * product does not keep. Mappings, Vorlagen, Sync and Validierung become tabs when
 * they become pages — the same rule `bexioNav` applies to the workspace itself.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import {
  fetchBexioConfig,
  fetchBexioBankAccountCatalog,
  fetchBexioCurrencyCatalog,
  fetchBexioPaymentTypeCatalog,
  fetchBexioLanguageCatalog,
  fetchBexioStatus,
  fetchBexioTaxCatalog,
  fetchBexioUserCatalog,
  updateBexioConfig,
  updateBexioTargetUser,
} from "../../../api/adminData/bexio";
import {
  fetchAdminBillingSettings,
  updateAdminBillingSettings,
} from "../../../api/adminData/billingSettings";
import { BexioTableError } from "../../../components/admin/bexio/workspace/BexioTableStates";
import { getTokenPayload } from "../../../authStore";
import BexioInvoiceTexts from "./settings/BexioInvoiceTexts";
import BexioSettingsGeneral from "./settings/BexioSettingsGeneral";
import {
  headerPatchValue,
  headerSaveErrorMessage,
  isHeaderDirty,
  normalizeLetterheadText,
} from "./bexioInvoiceTextsView";
import {
  buildDirtyPatch,
  configValuesToForm,
  formFieldErrors,
  isFormDirty,
  isTargetUserDirty,
  normalizeCatalogId,
  saveErrorMessage,
  targetUserSaveErrorMessage,
} from "./bexioSettingsView";

/**
 * The role, from the token, exactly as the other Bexio pages read it
 * (`BexioDraftsPage`, `BexioOverviewPage`, `AdminBexioPage`).
 *
 * The browser's answer only decides what to RENDER. The endpoint is admin-only
 * server-side, so a manipulated token buys a form, not a write.
 */
function isAdmin() {
  try {
    return getTokenPayload()?.role === "admin";
  } catch {
    return false;
  }
}

export default function BexioSettingsPage() {
  /* ── A. The stored configuration ───────────────────────────────────── */
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState("");
  const [configLoading, setConfigLoading] = useState(true);

  // The edit surface. `initial` is the saved baseline the diff is measured
  // against; `form` is what the operator currently sees.
  const [form, setForm] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const loadConfig = useCallback(() => {
    let alive = true;
    setConfigLoading(true);
    fetchBexioConfig()
      .then((res) => {
        if (!alive) return;
        setConfig(res);
        const next = configValuesToForm(res.values);
        setForm(next);
        setInitial(next);
        setSaveError("");
        setConfigError("");
      })
      .catch((e) => {
        if (!alive) return;
        setConfig(null);
        setForm(null);
        setInitial(null);
        setConfigError(e?.message || "Konfiguration konnte nicht geladen werden.");
      })
      .finally(() => {
        if (alive) setConfigLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => loadConfig(), [loadConfig]);

  /* ── B. Connection status (counts + last successful sync) ──────────── */
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setStatusLoading(true);
    fetchBexioStatus()
      .then((res) => {
        if (alive) setStatus(res);
      })
      .catch(() => {
        // Deliberately not surfaced as a page error: the status endpoint only
        // enriches the connection card. The configuration — the reason this page
        // exists — is unaffected, and its own error path above owns that report.
        if (alive) setStatus(null);
      })
      .finally(() => {
        if (alive) setStatusLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const notConfigured = !configLoading && !configError && config?.configured === false;
  const configured = config?.configured === true;

  /* ── C. The edit surface ───────────────────────────────────────────── */
  const admin = isAdmin();
  const canEdit = admin && configured && !configLoading && !configError;

  const fieldErrors = useMemo(() => (form ? formFieldErrors(form) : {}), [form]);
  const hasFieldError = Object.keys(fieldErrors).length > 0;
  const dirty = useMemo(() => isFormDirty(form, initial), [form, initial]);

  const handleFieldChange = useCallback((key, value) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  const canSave = canEdit && dirty && !hasFieldError && !saving;

  /* ── D. The provider catalogs (D3a currency, D3b tax, D3c bank, D3d pay,
   *        D3e language) ─────────────────────────────────────────────────
   *
   * INDEPENDENT states, one per catalog, alongside config and status. These are
   * the only calls on this page that reach Bexio live, so each gets its own
   * loading and error path: a provider outage must degrade one dropdown, never
   * the configuration the operator came here to read — and never the other
   * dropdown. They are separate `useState`/`useEffect` pairs rather than one
   * keyed record precisely so that is structural: nothing a tax request does can
   * write into the currency state.
   *
   * Loaded only for an admin with a configured org — a manager has no edit
   * controls, so fetching a selection aid for them would spend a provider
   * request on a write they cannot perform. Once per page lifecycle each, plus an
   * explicit retry; nothing here re-fetches after a save.
   */
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [currencyState, setCurrencyState] = useState("idle");
  const [currencyError, setCurrencyError] = useState("");

  const loadCurrencyCatalog = useCallback(() => {
    setCurrencyState("loading");
    setCurrencyError("");
    fetchBexioCurrencyCatalog()
      .then((res) => {
        setCurrencyOptions(Array.isArray(res?.options) ? res.options : []);
        setCurrencyState("loaded");
      })
      .catch((e) => {
        // The stored value stays exactly as it is — see BexioCatalogSelect.
        setCurrencyOptions([]);
        setCurrencyState("error");
        setCurrencyError(
          e?.status === 409
            ? "Bexio-Verbindung ist nicht aktiv."
            : "Die Bexio-Auswahlliste konnte nicht geladen werden."
        );
      });
  }, []);

  useEffect(() => {
    // Deliberately NOT chained to the config request: no Promise.all, so a slow
    // or failing catalog cannot hold up the configuration.
    if (canEdit && currencyState === "idle") loadCurrencyCatalog();
  }, [canEdit, currencyState, loadCurrencyCatalog]);

  const [taxOptions, setTaxOptions] = useState([]);
  const [taxState, setTaxState] = useState("idle");
  const [taxError, setTaxError] = useState("");

  const loadTaxCatalog = useCallback(() => {
    setTaxState("loading");
    setTaxError("");
    fetchBexioTaxCatalog()
      .then((res) => {
        setTaxOptions(Array.isArray(res?.options) ? res.options : []);
        setTaxState("loaded");
      })
      .catch((e) => {
        // Only this catalog's own state is touched. The stored `default_tax_id`
        // stays exactly as it is (see BexioCatalogSelect), the currency list is
        // untouched, and the form is never written to from here — a failed load
        // must not be able to clear a working tax setting.
        setTaxOptions([]);
        setTaxState("error");
        setTaxError(
          e?.status === 409
            ? "Bexio-Verbindung ist nicht aktiv."
            : "Die Bexio-Auswahlliste konnte nicht geladen werden."
        );
      });
  }, []);

  useEffect(() => {
    // Its own effect, not chained to the currency one and not a Promise.all: the
    // two requests start together and fail apart.
    if (canEdit && taxState === "idle") loadTaxCatalog();
  }, [canEdit, taxState, loadTaxCatalog]);

  const [bankAccountOptions, setBankAccountOptions] = useState([]);
  const [bankAccountState, setBankAccountState] = useState("idle");
  const [bankAccountError, setBankAccountError] = useState("");

  const loadBankAccountCatalog = useCallback(() => {
    setBankAccountState("loading");
    setBankAccountError("");
    fetchBexioBankAccountCatalog()
      .then((res) => {
        setBankAccountOptions(Array.isArray(res?.options) ? res.options : []);
        setBankAccountState("loaded");
      })
      .catch((e) => {
        // Only this catalog's own state. The stored `bank_account_id` stays as it
        // is, the other two option lists are untouched, and the form is never
        // written to from here — a failed load must not clear a working account.
        setBankAccountOptions([]);
        setBankAccountState("error");
        setBankAccountError(
          e?.status === 409
            ? "Bexio-Verbindung ist nicht aktiv."
            : "Die Bexio-Auswahlliste konnte nicht geladen werden."
        );
      });
  }, []);

  useEffect(() => {
    // Third independent effect. The three requests start together and fail apart.
    if (canEdit && bankAccountState === "idle") loadBankAccountCatalog();
  }, [canEdit, bankAccountState, loadBankAccountCatalog]);

  const [paymentTypeOptions, setPaymentTypeOptions] = useState([]);
  const [paymentTypeState, setPaymentTypeState] = useState("idle");
  const [paymentTypeError, setPaymentTypeError] = useState("");

  const loadPaymentTypeCatalog = useCallback(() => {
    setPaymentTypeState("loading");
    setPaymentTypeError("");
    fetchBexioPaymentTypeCatalog()
      .then((res) => {
        setPaymentTypeOptions(Array.isArray(res?.options) ? res.options : []);
        setPaymentTypeState("loaded");
      })
      .catch((e) => {
        // Only this catalog's own state. The stored `payment_type_id` stays as it
        // is, the other three option lists are untouched, and the form is never
        // written to from here — a failed load must not clear a working setting.
        setPaymentTypeOptions([]);
        setPaymentTypeState("error");
        setPaymentTypeError(
          e?.status === 409
            ? "Bexio-Verbindung ist nicht aktiv."
            : "Die Bexio-Auswahlliste konnte nicht geladen werden."
        );
      });
  }, []);

  useEffect(() => {
    // Fourth independent effect. The four requests start together and fail apart.
    if (canEdit && paymentTypeState === "idle") loadPaymentTypeCatalog();
  }, [canEdit, paymentTypeState, loadPaymentTypeCatalog]);

  const [languageOptions, setLanguageOptions] = useState([]);
  const [languageState, setLanguageState] = useState("idle");
  const [languageError, setLanguageError] = useState("");

  const loadLanguageCatalog = useCallback(() => {
    setLanguageState("loading");
    setLanguageError("");
    fetchBexioLanguageCatalog()
      .then((res) => {
        setLanguageOptions(Array.isArray(res?.options) ? res.options : []);
        setLanguageState("loaded");
      })
      .catch((e) => {
        // Only this catalog's own state. The stored `default_language_id` stays
        // as it is, the other four option lists are untouched, and the form is
        // never written to from here — a failed load must not clear a working
        // setting.
        setLanguageOptions([]);
        setLanguageState("error");
        setLanguageError(
          e?.status === 409
            ? "Bexio-Verbindung ist nicht aktiv."
            : "Die Bexio-Auswahlliste konnte nicht geladen werden."
        );
      });
  }, []);

  useEffect(() => {
    // Fifth independent effect. The five requests start together and fail apart.
    if (canEdit && languageState === "idle") loadLanguageCatalog();
  }, [canEdit, languageState, loadLanguageCatalog]);

  const [userOptions, setUserOptions] = useState([]);
  const [userState, setUserState] = useState("idle");
  const [userCatalogError, setUserCatalogError] = useState("");

  const loadUserCatalog = useCallback(() => {
    setUserState("loading");
    setUserCatalogError("");
    fetchBexioUserCatalog()
      .then((res) => {
        setUserOptions(Array.isArray(res?.options) ? res.options : []);
        setUserState("loaded");
      })
      .catch((e) => {
        // Only this catalog's own state. The stored `target_user_id` and the
        // name beside it stay exactly as they are — a failed load must never
        // clear the contact, and on this row that matters more than on the
        // others: production currently holds an id the provider no longer has,
        // and blanking it would destroy the only evidence of what was chosen.
        setUserOptions([]);
        setUserState("error");
        setUserCatalogError(
          e?.status === 409
            ? "Bexio-Verbindung ist nicht aktiv."
            : "Die Bexio-Auswahlliste konnte nicht geladen werden."
        );
      });
  }, []);

  useEffect(() => {
    // Sixth independent effect. The six requests start together and fail apart;
    // still no Promise.all, so the slowest cannot hold up the other five.
    if (canEdit && userState === "idle") loadUserCatalog();
  }, [canEdit, userState, loadUserCatalog]);

  const catalogs = useMemo(
    () => ({
      currency: {
        options: currencyOptions,
        state: currencyState,
        error: currencyError,
        onRetry: loadCurrencyCatalog,
      },
      taxes: {
        options: taxOptions,
        state: taxState,
        error: taxError,
        onRetry: loadTaxCatalog,
      },
      bankAccounts: {
        options: bankAccountOptions,
        state: bankAccountState,
        error: bankAccountError,
        onRetry: loadBankAccountCatalog,
      },
      paymentTypes: {
        options: paymentTypeOptions,
        state: paymentTypeState,
        error: paymentTypeError,
        onRetry: loadPaymentTypeCatalog,
      },
      languages: {
        options: languageOptions,
        state: languageState,
        error: languageError,
        onRetry: loadLanguageCatalog,
      },
      users: {
        options: userOptions,
        state: userState,
        error: userCatalogError,
        onRetry: loadUserCatalog,
      },
    }),
    [
      currencyOptions,
      currencyState,
      currencyError,
      loadCurrencyCatalog,
      taxOptions,
      taxState,
      taxError,
      loadTaxCatalog,
      bankAccountOptions,
      bankAccountState,
      bankAccountError,
      loadBankAccountCatalog,
      paymentTypeOptions,
      paymentTypeState,
      paymentTypeError,
      loadPaymentTypeCatalog,
      languageOptions,
      languageState,
      languageError,
      loadLanguageCatalog,
      userOptions,
      userState,
      userCatalogError,
      loadUserCatalog,
    ]
  );

  /* ── E. The internal contact, which saves on its own (D3f.2) ───────
   *
   * NOT PART OF `form`. Every other editable field is diffed by
   * `buildDirtyPatch` and sent by the header's "Änderungen speichern" through
   * `PATCH /config`, which never talks to Bexio. This one cannot go that way:
   * storing the id also means storing the NAME beside it, the browser may not
   * author that name, and only the server can resolve it — so its write asks
   * Bexio, and keeping it out of the shared form is what leaves the generic save
   * provider-free.
   *
   * `targetUserDraft` therefore shadows `config.values.target_user_id` rather
   * than living in `form`, and the header button can neither send it nor be lit
   * up by it.
   */
  const persistedTargetUser = normalizeCatalogId(
    config?.values?.target_user_id ?? null
  );
  // UNDEFINED MEANS UNTOUCHED, and that is what makes this correct rather than
  // merely convenient. Seeding a `useState` from the config in an effect leaves
  // one render where the draft is still null while the config already holds an
  // id: for that render the row shows "Nicht konfiguriert" instead of the stored
  // value, and — worse — reads as dirty, so the save button lights up for a
  // change nobody made. Deriving the displayed value instead removes the window
  // entirely; there is no moment at which the two disagree.
  //
  // It also gives Task-8's rule for free: an untouched draft FOLLOWS the stored
  // value, so a reload or an unrelated generic save updates the row by itself,
  // while a selection the operator has actually made survives both.
  const [targetUserDraft, setTargetUserDraft] = useState(undefined);
  const [targetUserSaving, setTargetUserSaving] = useState(false);
  const [targetUserError, setTargetUserError] = useState("");

  const targetUserValue =
    targetUserDraft === undefined ? persistedTargetUser : targetUserDraft;
  const targetUserDirty = isTargetUserDirty(targetUserValue, persistedTargetUser);

  const handleTargetUserChange = useCallback((next) => {
    setTargetUserDraft(next);
    // The previous failure described the previous attempt; keeping it beside a
    // changed selection would misreport which one failed.
    setTargetUserError("");
  }, []);

  const handleSaveTargetUser = useCallback(async () => {
    // A guard in its own right, not just a disabled button: a re-render between
    // the click and the request can re-enable it.
    if (targetUserSaving || !canEdit || !targetUserDirty) return;

    setTargetUserSaving(true);
    setTargetUserError("");
    try {
      // ONE FIELD. Never the catalog option, never its label — the server
      // resolves the name, which is what keeps the stored name and the stored id
      // describing the same person.
      const res = await updateBexioTargetUser({
        target_user_id: targetUserValue,
      });
      // The response is the whole stored configuration, recomputed server-side,
      // so it becomes the new truth — the resolved display name included, which
      // is what refreshes the summary card. No second GET.
      setConfig(res);
      const next = configValuesToForm(res.values);
      setForm(next);
      setInitial(next);
      // Back to "untouched", so the row now follows whatever the server stored —
      // which is the resolved value, not necessarily the one that was sent.
      setTargetUserDraft(undefined);
      toast.success("Ansprechpartner gespeichert.");
    } catch (e) {
      // The configuration is deliberately NOT touched, and the draft selection
      // stays visible: a failure must cost the operator neither their stored
      // contact nor the choice they were making. The stale id in particular has
      // to survive — it is the only record of what was configured.
      const message = targetUserSaveErrorMessage(e);
      setTargetUserError(message);
      toast.error(message);
    } finally {
      setTargetUserSaving(false);
    }
  }, [targetUserSaving, canEdit, targetUserDirty, targetUserValue]);

  const targetUser = useMemo(
    () =>
      canEdit
        ? {
            draft: targetUserValue,
            dirty: targetUserDirty,
            saving: targetUserSaving,
            error: targetUserError,
            onChange: handleTargetUserChange,
            onSave: handleSaveTargetUser,
          }
        : null,
    [
      canEdit,
      targetUserValue,
      targetUserDirty,
      targetUserSaving,
      targetUserError,
      handleTargetUserChange,
      handleSaveTargetUser,
    ]
  );

  /* ── F. Invoice letterhead texts (D4.1a) ────────────────────────────
   *
   * A DIFFERENT PERSISTENCE DOMAIN ON THE SAME PAGE. Header and footer live on
   * `organization_billing_settings`, not on `bexio_connections`, and are read and
   * written through `GET`/`PATCH /api/admin/billing-settings`. Nothing on this path
   * builds a Bexio client or reads a token, so a provider outage must not block the
   * header save — hence its own state, its own request and its own save control
   * rather than a seat in the generic config form.
   *
   * ADMIN-ONLY, AND THAT IS A BACKEND FACT, NOT A UI CHOICE. Both the GET and the
   * PATCH are `require_roles("admin")` (routes_admin_billing_settings.py:291 / :305),
   * so a manager cannot read these values at all. Fetching for them would spend a
   * request on a guaranteed 403 and render a section with nothing in it; the section
   * is therefore not rendered for a manager. That matches how this page already
   * handles the config form — no disabled inputs promising an edit the endpoint
   * refuses — and here it is stronger, because there is not even a value to show.
   *
   * NOT chained to the config request and not part of any catalog state machine:
   * no `Promise.all`, so a slow or failing billing-settings read cannot hold up or
   * blank the provider configuration.
   */
  const [billingTextSettings, setBillingTextSettings] = useState(null);
  const [billingTextLoading, setBillingTextLoading] = useState(false);
  const [billingTextError, setBillingTextError] = useState("");

  const loadBillingTextSettings = useCallback(() => {
    setBillingTextLoading(true);
    setBillingTextError("");
    fetchAdminBillingSettings()
      .then((res) => {
        setBillingTextSettings(res);
      })
      .catch((e) => {
        setBillingTextSettings(null);
        setBillingTextError(
          e?.message || "Die Rechnungstexte konnten nicht geladen werden."
        );
      })
      .finally(() => setBillingTextLoading(false));
  }, []);

  useEffect(() => {
    if (!admin) return;
    loadBillingTextSettings();
  }, [admin, loadBillingTextSettings]);

  // `undefined` means "the operator has not typed", which is NOT the same as an
  // empty header: the stored value may be null, "" or text, and until they touch the
  // control the persisted value is what shows. Seeding a draft from an effect would
  // leave one render with an empty draft while the settings already held text, and
  // that render would read as a pending change and light the save button.
  const [headerDraft, setHeaderDraft] = useState(undefined);
  const [headerSaving, setHeaderSaving] = useState(false);
  const [headerError, setHeaderError] = useState("");

  const persistedHeader = normalizeLetterheadText(
    billingTextSettings?.default_header_text
  );
  const headerValue =
    headerDraft === undefined ? persistedHeader : headerDraft;
  const headerDirty = isHeaderDirty(headerValue, persistedHeader);

  const canEditTexts =
    admin && !billingTextLoading && !billingTextError && billingTextSettings != null;

  const handleHeaderChange = useCallback((value) => {
    setHeaderDraft(value);
    setHeaderError("");
  }, []);

  const handleSaveHeader = useCallback(async () => {
    if (headerSaving || !canEditTexts || !headerDirty) return;
    setHeaderSaving(true);
    setHeaderError("");
    try {
      // MINIMAL PAYLOAD. Only the field this control owns. Sending the whole
      // settings record would let this page overwrite the footer — and every other
      // billing setting — with whatever it happened to have read earlier.
      const res = await updateAdminBillingSettings({
        default_header_text: headerPatchValue(headerValue),
      });
      // The response is the whole stored row, so it becomes the new truth for the
      // header AND the displayed footer. No second GET.
      setBillingTextSettings(res);
      setHeaderDraft(undefined);
      toast.success("Kopfzeile gespeichert.");
    } catch (e) {
      // The draft is left exactly as typed: a failed save must not cost the edit,
      // and `billingTextSettings` stays put so the change is still pending.
      const message = headerSaveErrorMessage(e);
      setHeaderError(message);
      toast.error(message);
    } finally {
      setHeaderSaving(false);
    }
  }, [headerSaving, canEditTexts, headerDirty, headerValue]);

  const handleSave = useCallback(async () => {
    // Guard in its own right, not just a disabled button: the button can be
    // re-enabled by a re-render between the click and the request.
    if (saving || !canEdit || hasFieldError) return;
    const patch = buildDirtyPatch(form, initial);
    if (Object.keys(patch).length === 0) return;

    setSaving(true);
    setSaveError("");
    try {
      const res = await updateBexioConfig(patch);
      // The response is the whole stored configuration, recomputed server-side,
      // so it becomes the new truth — blockers, completeness and the
      // server-owned target-user label included. No second GET.
      setConfig(res);
      const next = configValuesToForm(res.values);
      setForm(next);
      setInitial(next);
      toast.success("Einstellungen gespeichert.");
    } catch (e) {
      // The form is deliberately left exactly as the operator typed it: an
      // error must never cost them their edits, and `initial` stays put so the
      // change is still pending rather than silently accepted.
      const message = saveErrorMessage(e);
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }, [saving, canEdit, hasFieldError, form, initial]);

  return (
    <div className="p-4 sm:p-6">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-foreground">Einstellungen</h1>
          <p className="mt-1 max-w-[720px] text-sm text-muted-foreground">
            Die für Bexio-Rechnungsentwürfe hinterlegte Konfiguration dieser
            Organisation.
          </p>
        </div>
        {/* Not rendered at all for a manager or an unconfigured org: a disabled
            button there would advertise an action neither can ever take. */}
        {canEdit ? (
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? "Speichert …" : "Änderungen speichern"}
          </button>
        ) : null}
      </header>

      {saveError ? (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
        >
          {saveError}
        </p>
      ) : null}

      {configError ? (
        <div className="rounded-xl border border-border bg-card">
          <BexioTableError message={configError} onRetry={loadConfig} />
        </div>
      ) : notConfigured ? (
        <section
          aria-label="Bexio nicht konfiguriert"
          className="rounded-xl border border-border bg-card p-6"
        >
          <h2 className="text-sm font-semibold text-foreground">
            Bexio ist für diese Organisation noch nicht konfiguriert.
          </h2>
          <p className="mt-2 max-w-[640px] text-sm text-muted-foreground">
            Es ist keine Bexio-Verbindung hinterlegt. Solange das so ist, erstellt
            Vantio keine Rechnungsentwürfe in Bexio.
          </p>
          <p className="mt-2 max-w-[640px] text-xs text-muted-foreground">
            Das Einrichten der Verbindung ist ein eigener, bewusster Schritt und
            geschieht nicht automatisch durch das Öffnen dieser Seite.
          </p>
        </section>
      ) : (
        <BexioSettingsGeneral
          config={config}
          configLoading={configLoading}
          status={status}
          statusLoading={statusLoading}
          form={form}
          fieldErrors={fieldErrors}
          canEdit={canEdit}
          onFieldChange={handleFieldChange}
          catalogs={catalogs}
          targetUser={targetUser}
        />
      )}

      {/* OUTSIDE the config branch above, deliberately — but NOT unconditionally.
          These texts are stored on `organization_billing_settings` and written
          through a different endpoint, so a FAILED config read must not disable a
          save that never touches the provider; nesting this in the `else` would do
          exactly that, which is why it sits here instead.

          `notConfigured` is the one case that does suppress it, and it is not the
          same thing as an outage: it means the org has deliberately not set Bexio
          up at all. That state renders one explicit card saying so and offers
          nothing else — no form, no save — because setting the connection up is a
          separate, deliberate step. A "Kopfzeile speichern" button there would
          configure the wording of invoices this org does not create, and would be
          the first control to appear in a state whose whole point is that it has
          none.

          Admin-only because the billing-settings GET is (see the state block
          above); its own section rather than a row inside the config card, because
          burying it among the provider ids would claim it is provider config. */}
      {admin && !notConfigured ? (
        <BexioInvoiceTexts
          loading={billingTextLoading}
          error={billingTextError}
          onRetry={loadBillingTextSettings}
          headerDraft={headerValue}
          footerText={billingTextSettings?.default_footer_text ?? null}
          canEdit={canEditTexts}
          dirty={headerDirty}
          saving={headerSaving}
          saveError={headerError}
          onHeaderChange={handleHeaderChange}
          onSave={handleSaveHeader}
        />
      ) : null}
    </div>
  );
}
